import { useState } from "react";
import { api, formatDate, formatRelative } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, MessageCircle, Trash2, CheckCircle2, Clock, Pause, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  tamamlandi: { label: "Tamamlandı", icon: CheckCircle2, color: "#10b981", cls: "success" },
  devam: { label: "Devam ediyor", icon: Clock, color: "#f59e0b", cls: "warning" },
  beklemede: { label: "Beklemede", icon: Pause, color: "#64748b", cls: "info" },
};

export default function ReportCard({ report, onChanged, compact = false }) {
  const { user, isAdmin } = useAuth();
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [likingLocal, setLikingLocal] = useState({ count: report.likes_count || 0, liked: !!report.liked_by_me });

  const toggleLike = async () => {
    // optimistic
    setLikingLocal((p) => ({ count: p.liked ? p.count - 1 : p.count + 1, liked: !p.liked }));
    try {
      await api.post(`/reports/${report.id}/like`);
    } catch {
      // revert
      setLikingLocal((p) => ({ count: p.liked ? p.count - 1 : p.count + 1, liked: !p.liked }));
      toast.error("İşlem başarısız");
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/reports/${report.id}/comments`, { text: comment });
      setComment("");
      toast.success("Yorum eklendi");
      onChanged?.();
    } catch { toast.error("Yorum eklenemedi"); }
    finally { setPosting(false); }
  };

  const deleteComment = async (id) => {
    if (!window.confirm("Yorumu silmek istiyor musunuz?")) return;
    try {
      await api.delete(`/comments/${id}`);
      onChanged?.();
    } catch { toast.error("Silinemedi"); }
  };

  const deleteReport = async () => {
    if (!window.confirm("Bu raporu silmek istiyor musunuz?")) return;
    try {
      await api.delete(`/reports/${report.id}`);
      toast.success("Rapor silindi");
      onChanged?.();
    } catch { toast.error("Silinemedi"); }
  };

  const author = report.author || {};
  const isMyReport = report.user_id === user?.id;
  const tasks = report.tasks || [];
  const comments = report.comments || [];

  return (
    <div className="report-card" data-testid={`report-${report.id}`}>
      <header className="report-head">
        <div className="rc-avatar">{(author.name || "?").charAt(0)}</div>
        <div className="rc-identity">
          <strong>{author.name || "Silinmiş kullanıcı"}</strong>
          <small>{author.position || "—"} · {formatDate(report.date)}</small>
        </div>
        <div className="rc-meta">
          <span className="rc-hours">{report.hours_worked || 0}h</span>
          {(isMyReport || isAdmin) && (
            <button className="icon-btn danger" onClick={deleteReport} title="Raporu sil" data-testid={`delete-report-${report.id}`}>
              <Trash2 size={14}/>
            </button>
          )}
        </div>
      </header>

      {report.free_text && (
        <p className="report-text" data-testid={`report-text-${report.id}`}>{report.free_text}</p>
      )}

      {tasks.length > 0 && (
        <ul className="task-list" data-testid={`report-tasks-${report.id}`}>
          {tasks.map((t, i) => {
            const meta = STATUS_META[t.status] || STATUS_META.beklemede;
            const Icon = meta.icon;
            return (
              <li key={i} className={`task-item status-${t.status}`}>
                <Icon size={14} style={{ color: meta.color, flexShrink: 0 }}/>
                <div>
                  <span className="task-title">{t.title}</span>
                  {t.note && <small className="task-note">{t.note}</small>}
                </div>
                <span className={`badge ${meta.cls}`} style={{ marginLeft: "auto", flexShrink: 0 }}>{meta.label}</span>
              </li>
            );
          })}
        </ul>
      )}

      {!compact && (
        <footer className="report-foot">
          <div className="rc-actions">
            <button className={`react-btn ${likingLocal.liked ? "liked" : ""}`}
              onClick={toggleLike} data-testid={`like-${report.id}`}>
              <Heart size={14} fill={likingLocal.liked ? "#ef4444" : "none"}/>
              <span>{likingLocal.count}</span>
            </button>
            <span className="react-btn" title="Yorumlar">
              <MessageCircle size={14}/><span>{comments.length}</span>
            </span>
            <span className="rc-time">{formatRelative(report.updated_at || report.created_at)}</span>
          </div>

          {comments.length > 0 && (
            <ul className="comment-list">
              {comments.map((c) => (
                <li key={c.id} data-testid={`comment-${c.id}`}>
                  <div className="comment-head">
                    <strong>{c.author_name}</strong>
                    <small>{formatRelative(c.created_at)}</small>
                    {(c.user_id === user?.id || isAdmin) && (
                      <button className="icon-btn-tiny" onClick={() => deleteComment(c.id)} title="Sil">
                        <Trash2 size={11}/>
                      </button>
                    )}
                  </div>
                  <p>{c.text}</p>
                </li>
              ))}
            </ul>
          )}

          <form className="comment-form" onSubmit={addComment}>
            <input
              type="text"
              placeholder="Yorum yaz…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={2000}
              data-testid={`comment-input-${report.id}`}
            />
            <button type="submit" disabled={posting || !comment.trim()} data-testid={`comment-submit-${report.id}`}>
              <Send size={13}/>
            </button>
          </form>
        </footer>
      )}
    </div>
  );
}
